import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Sparkle, PaperPlaneRight, DownloadSimple, Robot,
  ArrowClockwise, Paperclip, FileDoc, Plus, Trash, ChatCircleText,
  PencilSimple, Check, X,
} from "@phosphor-icons/react";

/**
 * Iter178 → Iter208.31 — AI Copilot con storico multi-conversazione.
 * - Sidebar a sinistra con lista conversazioni salvate + titolo autogenerato.
 * - Bottone "Nuova conversazione" in cima.
 * - Ogni conversazione ha: riapri (click sul titolo) + elimina (icona cestino).
 * - Il modello riceve solo la history della conversazione attiva.
 * - LocalStorage `ai_active_session_<pid>` ricorda l'ultima sessione aperta.
 */

// Iter209aj — Regex per il marker OPTIONS del bot.
// Formato: `[[OPTIONS:domanda|Opzione1|Opzione2|...]]`.
// Il primo token è la label della domanda, gli altri sono i bottoni.
const OPTIONS_MARKER_RE = /\[\[OPTIONS:([^\]]*)\]\]/;

// Iter209ak — Regex per il marker GENERATE_DOC del bot.
// Formato: `[[GENERATE_DOC:kind|titolo]]`. Il backend intercetta il marker,
// genera il documento con Claude e restituisce un `attachment`. Nel
// frontend il marker deve essere rimosso dal testo visibile — sia
// durante lo streaming token per token, sia a fine risposta — così
// non appare mai come testo grezzo nella chat.
const GENERATE_DOC_MARKER_RE = /\[\[GENERATE_DOC\s*:\s*[a-z0-9_]+\s*(?:\|[^\]]+)?\]\]/gi;
// Marker parziale (durante lo streaming): il modello potrebbe mandare
// prima `[[GENERATE` poi il resto dei token. Nascondiamo l'apertura
// finché non arriva il `]]` finale.
const GENERATE_DOC_PARTIAL_RE = /\[\[GENERATE(?:_DOC[^\]]*)?$/i;

function stripGenerateDocMarker(text) {
  if (!text) return text;
  return text
    .replace(GENERATE_DOC_MARKER_RE, "")
    .replace(GENERATE_DOC_PARTIAL_RE, "")
    .replace(/[ \t]+\n/g, "\n");
}

function parseOptionsMarker(text) {
  if (!text) return null;
  const m = text.match(OPTIONS_MARKER_RE);
  if (!m) return null;
  const parts = (m[1] || "").split("|").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  const [question, ...options] = parts;
  return {
    before: text.slice(0, m.index).replace(/[\s\n]+$/, ""),
    after: text.slice(m.index + m[0].length).replace(/^[\s\n]+/, ""),
    question,
    options,
  };
}

export default function AiCopilotSection({ propertyId, onDocumentsUploaded }) {  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [longWait, setLongWait] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Iter208.31 — Multi-sessione
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  // Iter208.39 — Rinomina manuale
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef(null);
  const listRef = useRef(null);

  const LS_KEY = propertyId ? `ai_active_session_${propertyId}` : null;

  // Iter208.59 — Ref stabili per lo stato "corrente" senza mettere valori
  // che cambiano ad ogni token nello useCallback deps: senza questo, il
  // bootstrap effect si ri-eseguiva a metà streaming e sovrascriveva la
  // conversazione con la history del DB (che non aveva ancora l'assistente).
  const activeSessionIdRef = useRef(null);
  const messagesRef = useRef([]);
  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Iter208.31 — Fetch lista sessioni per l'immobile.
  const reloadSessions = useCallback(async () => {
    if (!propertyId) return [];
    setLoadingSessions(true);
    try {
      const r = await api.get(`/properties/${propertyId}/ai-chat/sessions`);
      const list = r.data?.sessions || [];
      setSessions(list);
      return list;
    } catch (_e) {
      setSessions([]);
      return [];
    } finally {
      setLoadingSessions(false);
    }
  }, [propertyId]);

  // Iter208.31 — Carica la history di una sessione specifica.
  // Iter208.59 — Nessuna dipendenza su activeSessionId/messages (usa ref)
  // per evitare re-creazioni che triggeravano ricariche mid-stream.
  const loadSessionHistory = useCallback(async (sid) => {
    if (!propertyId || !sid) {
      setMessages([]);
      setActiveSessionId(null);
      return;
    }
    // Se stiamo lasciando una sessione con messaggi, finalizza il titolo.
    const prevSid = activeSessionIdRef.current;
    const prevLen = messagesRef.current.length;
    if (prevSid && prevSid !== sid && prevLen > 0) {
      try {
        api.post(
          `/properties/${propertyId}/ai-chat/sessions/${prevSid}/finalize-title`,
          {},
          { timeout: 15000 },
        ).catch(() => {});
      } catch (_e) { /* silent */ }
    }
    try {
      const r = await api.get(`/properties/${propertyId}/ai-chat/history`, {
        params: { session_id: sid },
      });
      setMessages(r.data?.messages || []);
      setActiveSessionId(sid);
      if (LS_KEY) localStorage.setItem(LS_KEY, sid);
    } catch (_e) {
      setMessages([]);
    }
  }, [propertyId, LS_KEY]);

  // Iter208.31 — Bootstrap: fetch sessioni, poi apri quella più recente
  // (o quella in localStorage se ancora esiste), altrimenti chat vuota.
  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      const list = await reloadSessions();
      const stored = LS_KEY ? localStorage.getItem(LS_KEY) : null;
      const target =
        (stored && list.find((s) => s._id === stored)) ||
        list[0] ||
        null;
      if (target) {
        await loadSessionHistory(target._id);
      } else {
        setMessages([]);
        setActiveSessionId(null);
      }
    })();
  }, [propertyId, reloadSessions, loadSessionHistory, LS_KEY]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading]);

  // Iter209dzj / Iter209dzm — Polling delle generazioni documento "in elaborazione".
  //
  // Quando il frontend ricarica l'history (es. refresh pagina) e trova
  // un messaggio assistente con `status: "generating"` significa che la
  // task detached sul backend sta ancora completando la generazione.
  // Qui pollariamo `GET /ai-chat/doc-status/{message_id}` ogni 3s per
  // ciascun messaggio in elaborazione; appena lo stato diventa
  // `completed` (o `failed`) aggiorniamo il messaggio in locale con il
  // body + attachment finali e fermiamo il polling.
  //
  // Iter209dzm hardening (dal code review):
  //   1. Dipendenza stabile: `generatingKey` = stringhe degli id in
  //      stato generating, serializzate. Così un edit qualunque dei
  //      messaggi (nuovo token in stream, nuovo utente msg) NON
  //      recreato tutti i timer con fresh 3s delay.
  //   2. Cap sui tentativi: dopo `MAX_POLL_ATTEMPTS` (3 min a 3s/tick)
  //      flipppiamo lo stato locale a `failed` così la UI non resta in
  //      loading infinito se il server a sua volta non ha ancora
  //      finalizzato lo stato (p. es. clock skew o worker killed senza
  //      reaper raggiunto). Il reaper server-side a 5 min copre lo
  //      scenario simmetrico.
  const generatingKey = useRef("");
  useEffect(() => {
    if (!propertyId) return undefined;
    const generatingIds = messages
      .filter((m) => m.role === "assistant" && m.status === "generating" && m._id)
      .map((m) => m._id);
    const key = generatingIds.join("|");
    // Se il set di id in generating è lo stesso del render precedente
    // non facciamo nulla: evita re-mount dei timer su ogni setMessages.
    if (key === generatingKey.current) return undefined;
    generatingKey.current = key;
    if (generatingIds.length === 0) return undefined;

    const MAX_POLL_ATTEMPTS = 60;  // 60 * 3s = 180s = 3 min
    let cancelled = false;
    const timers = generatingIds.map((mid) => {
      const tid = { current: null };
      let attempts = 0;
      const tick = async () => {
        if (cancelled) return;
        attempts += 1;
        try {
          const r = await api.get(
            `/properties/${propertyId}/ai-chat/doc-status/${mid}`,
          );
          const data = r.data || {};
          if (cancelled) return;
          if (data.status === "completed" || data.status === "failed") {
            setMessages((ms) => ms.map((mm) => (
              mm._id === mid
                ? {
                    ...mm,
                    body: data.body ?? mm.body,
                    status: data.status,
                    attachment: data.attachment || mm.attachment,
                    error: data.status === "failed",
                  }
                : mm
            )));
            return;  // ferma il polling di questo messaggio
          }
        } catch (_e) {
          // 404 o errore rete: proseguiamo con il prossimo tick, il
          // backend potrebbe non aver ancora scritto lo stato.
        }
        if (attempts >= MAX_POLL_ATTEMPTS) {
          // Cap raggiunto: segniamo il messaggio come failed locale
          // così la UI stacca lo spinner e mostra l'errore. Il reaper
          // server (`_DOCGEN_STALE_AFTER_SECONDS`) finalizza anche
          // Mongo al prossimo poll di un altro device.
          if (!cancelled) {
            setMessages((ms) => ms.map((mm) => (
              mm._id === mid
                ? {
                    ...mm,
                    status: "failed",
                    error: true,
                    body: (mm.body || "") +
                      "\n\n⚠️ Generazione in timeout: ricarica la pagina o riprova.",
                  }
                : mm
            )));
          }
          return;
        }
        if (!cancelled) tid.current = setTimeout(tick, 3000);
      };
      tid.current = setTimeout(tick, 3000);
      return tid;
    });
    return () => {
      cancelled = true;
      timers.forEach((t) => { if (t.current) clearTimeout(t.current); });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, propertyId]);

  // Iter208.38 — Finalizza il titolo della sessione attiva su cambio pagina
  // (beforeunload / unmount / cambio propertyId). Il titolo resta "Nuova chat"
  // finché la conversazione è aperta; il backend lo genera solo qui.
  const finalizeTitle = useCallback((pid, sid) => {
    if (!pid || !sid) return;
    // Fire-and-forget: usa `keepalive` per il beforeunload così la richiesta
    // parte anche se la pagina si sta chiudendo.
    try {
      api.post(
        `/properties/${pid}/ai-chat/sessions/${sid}/finalize-title`,
        {},
        { timeout: 15000 },
      ).catch(() => {});
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    const pid = propertyId;
    // Memorizza il session id vivo (chiusura closure)
    const onBeforeUnload = () => {
      if (activeSessionId && messages.length > 0) {
        // sendBeacon compat per garantire l'invio in fase di unload
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/properties/${pid}/ai-chat/sessions/${activeSessionId}/finalize-title`;
          const token = localStorage.getItem("ht_token") || "";
          const blob = new Blob([JSON.stringify({})], { type: "application/json" });
          if (navigator.sendBeacon && !token) {
            navigator.sendBeacon(url, blob);
          } else {
            // Fallback: fetch keepalive
            fetch(url, {
              method: "POST",
              headers: token
                ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
                : { "Content-Type": "application/json" },
              body: JSON.stringify({}),
              keepalive: true,
            }).catch(() => {});
          }
        } catch (_e) { /* silent */ }
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      // Unmount (cambio route / cambio immobile): finalizza la sessione attiva
      if (activeSessionId && messages.length > 0) {
        finalizeTitle(pid, activeSessionId);
      }
    };
  }, [propertyId, activeSessionId, messages.length, finalizeTitle]);

  // Iter208.31 — Nuova conversazione: reset stato + eventuale creazione lazy
  // (la sessione viene creata al primo send()).
  const startNewConversation = () => {
    // Iter208.38 — se stavamo chattando in una sessione con messaggi,
    // finalizza il titolo prima di crearne una nuova.
    if (activeSessionId && messages.length > 0) {
      finalizeTitle(propertyId, activeSessionId);
    }
    setMessages([]);
    setActiveSessionId(null);
    setInput("");
    if (LS_KEY) localStorage.removeItem(LS_KEY);
  };

  // Iter208.31 — Elimina conversazione dallo storico.
  const deleteSession = async (sid) => {
    if (!confirm("Eliminare questa conversazione? L'operazione è irreversibile.")) return;
    try {
      await api.delete(`/properties/${propertyId}/ai-chat/sessions/${sid}`);
      toast.success("Conversazione eliminata");
      if (sid === activeSessionId) {
        startNewConversation();
      }
      await reloadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  // Iter208.39 — Rinomina manuale conversazione (inline edit).
  const startRename = (sid, currentTitle) => {
    setRenamingId(sid);
    setRenameValue(currentTitle || "");
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };
  const saveRename = async (sid) => {
    const newTitle = renameValue.trim();
    if (!newTitle) {
      toast.error("Il titolo non può essere vuoto");
      return;
    }
    // Se non è cambiato, chiudi soltanto.
    const current = sessions.find((s) => s._id === sid);
    if (current && (current.title || "") === newTitle) {
      cancelRename();
      return;
    }
    try {
      await api.patch(`/properties/${propertyId}/ai-chat/sessions/${sid}`, { title: newTitle });
      // Aggiornamento ottimistico locale
      setSessions((list) => list.map((s) => (s._id === sid ? { ...s, title: newTitle } : s)));
      cancelRename();
      toast.success("Titolo aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rinomina");
    }
  };

  const send = async (overrideText) => {
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!text || loading) return;
    setLoading(true);
    setLongWait(false);
    // Iter208.40 — Aggiungi il messaggio utente + un placeholder assistente
    // che verrà riempito mano a mano che arrivano i token dallo stream SSE.
    const asstId_initial = `a_${Date.now()}`;
    let asstId = asstId_initial;
    setMessages((m) => [
      ...m,
      { role: "user", body: text, _id: `tmp_${Date.now()}` },
      { role: "assistant", body: "", _id: asstId_initial, streaming: true, attachment: null },
    ]);
    setInput("");
    const longWaitTimer = setTimeout(() => setLongWait(true), 12000);

    try {
      const token = localStorage.getItem("ht_token") || "";
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}/ai-chat/stream`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: text,
          session_id: activeSessionIdRef.current || undefined,
        }),
      });
      if (!resp.ok || !resp.body) {
        const errTxt = await resp.text().catch(() => "");
        throw new Error(errTxt || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buf = "";
      let currentReply = "";
      let currentAttachment = null;
      // Iter208.40 — Loop lettura frame SSE `data: {...}\n\n`
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Estrai i frame completi (separati da doppia newline)
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = raw.startsWith("data: ") ? raw.slice(6) : raw;
          if (!line.trim()) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === "session" && ev.session_id) {
              if (ev.session_id !== activeSessionIdRef.current) {
                // Aggiorna sincronamente il ref prima di qualsiasi altra
                // elaborazione: cosi un invio immediato successivo userebbe
                // la sessione corretta anche se setState e' ancora in coda.
                activeSessionIdRef.current = ev.session_id;
                setActiveSessionId(ev.session_id);
                if (LS_KEY) localStorage.setItem(LS_KEY, ev.session_id);
              }
            } else if (ev.type === "token" && typeof ev.text === "string") {
              currentReply += ev.text;
              setMessages((m) => m.map((mm) => mm._id === asstId ? { ...mm, body: currentReply } : mm));
            } else if (ev.type === "attachment" && ev.attachment) {
              currentAttachment = ev.attachment;
              setMessages((m) => m.map((mm) => mm._id === asstId ? { ...mm, attachment: currentAttachment } : mm));
            } else if (ev.type === "generating_doc") {
              // Segnala che sta preparando il .docx (Claude) — non serve UI extra
            } else if (ev.type === "doc_started" && ev.message_id) {
              // Iter209dzj — Il backend ha persistito un placeholder
              // su `ai_copilot_messages` con `status: "generating"`.
              // Allineiamo l'`_id` locale all'id Mongo così:
              //   1) il successivo UPDATE di body/attachment/status via
              //      polling matcha lo stesso messaggio;
              //   2) se l'utente refresha la pagina ora, lo stesso
              //      messaggio verrà ritrovato nella history.
              setMessages((m) => m.map((mm) => mm._id === asstId
                ? {
                    ...mm,
                    _id: ev.message_id,
                    status: "generating",
                    generation: { kind: ev.kind, title: ev.title },
                  }
                : mm));
              asstId = ev.message_id;
            } else if (ev.type === "progress" && typeof ev.message === "string") {
              // Iter209v — Il backend narra le fasi di generazione documento
              // ("Sto leggendo i documenti…", "Sto scrivendo…", "Sto
              // rifinendo…"). Salviamo il messaggio corrente sull'assistente
              // così l'UI può mostrarlo come strip italiana sotto il body.
              const stage = ev.stage || "progress";
              const message = ev.message;
              setMessages((m) => m.map((mm) => mm._id === asstId
                ? { ...mm, progress: { stage, message } }
                : mm));
            } else if (ev.type === "done") {
              currentReply = ev.reply || currentReply;
              currentAttachment = ev.attachment || currentAttachment;
              setMessages((m) => m.map((mm) => mm._id === asstId
                ? {
                    ...mm,
                    body: currentReply,
                    attachment: currentAttachment,
                    streaming: false,
                    progress: null,
                    status: mm.status === "generating" ? "completed" : mm.status,
                  }
                : mm));
            } else if (ev.type === "error") {
              throw new Error(ev.error || "Errore streaming");
            }
          } catch (parseErr) {
            // Ignora frame malformati
          }
        }
      }
      // Chiudi streaming flag se non arrivato "done"
      setMessages((m) => m.map((mm) => mm._id === asstId ? { ...mm, streaming: false } : mm));
      // Aggiorna il contatore messaggi in sidebar
      setTimeout(() => { reloadSessions(); }, 500);
    } catch (e) {
      const detail = e?.message || "Errore AI";
      toast.error(detail);
      setMessages((m) => m.map((mm) => mm._id === asstId
        ? { ...mm, body: `⚠️ ${detail}`, error: true, streaming: false }
        : mm));
    } finally {
      clearTimeout(longWaitTimer);
      setLongWait(false);
      setLoading(false);
    }
  };

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    // Iter186 — messaggio di caricamento visibile durante l'upload (utile per il D&D)
    const loadingId = `sys_loading_${Date.now()}`;
    setMessages((m) => [...m, {
      role: "system",
      body: files.length > 1
        ? `⏳ Caricamento di ${files.length} file in corso…`
        : `⏳ Caricamento di "${files[0].name}" in corso…`,
      _id: loadingId,
    }]);
    setUploading(true);
    try {
      let ok = 0;
      const uploadedNames = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        form.append("category", "ai_context");
        try {
          await api.post(`/properties/${propertyId}/documents`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          ok += 1;
          uploadedNames.push(f.name);
        } catch (e) { /* skip singolo */ }
      }
      // Rimuovo il placeholder di caricamento
      setMessages((m) => m.filter((x) => x._id !== loadingId));
      if (ok > 0) {
        toast.success(`${ok} file caricato/i. L'Agente ora ne è a conoscenza.`);
        onDocumentsUploaded?.();
        // Iter209dz2 — Persisti un ack REALE nella sessione cosi' il
        // documento entra nella history LlmChat del prossimo turno, e
        // forza il RAG per quel turno.
        let ackBody = ok > 1
          ? `Perfetto, ho ricevuto ${ok} nuovi documenti e li ho aggiunti alla mia memoria. Chiedimi pure cosa vuoi generare o sapere.`
          : `Perfetto, ho ricevuto il nuovo documento e l'ho aggiunto alla mia memoria. Chiedimi pure cosa vuoi generare o sapere.`;
        try {
          const { data } = await api.post(
            `/properties/${propertyId}/ai-chat/upload-ack`,
            {
              session_id: activeSessionIdRef.current || undefined,
              filenames: uploadedNames,
            },
          );
          if (data?.body) ackBody = data.body;
          if (data?.session_id && data.session_id !== activeSessionIdRef.current) {
            activeSessionIdRef.current = data.session_id;
            setActiveSessionId(data.session_id);
            if (LS_KEY) localStorage.setItem(LS_KEY, data.session_id);
          }
          const ackId = data?.ack_message_id || `ai_ack_${Date.now()}`;
          setMessages((m) => [...m, {
            role: "assistant",
            body: ackBody,
            _id: ackId,
          }]);
        } catch (e) {
          // Fallback: se la persistenza fallisce, mostra almeno l'ack locale
          setMessages((m) => [...m, {
            role: "assistant",
            body: ackBody,
            _id: `ai_ack_${Date.now()}`,
          }]);
        }
      } else {
        toast.error("Nessun file caricato");
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Iter186 — Drag & Drop di file sulla chat.
  // Contatore per gestire enter/leave sui figli nidificati senza flickering.
  const onDragEnter = (e) => {
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (!uploading) setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e) => {
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    if (!e.dataTransfer) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      upload(files);
    }
  };

  return (
    <div className="kpi-card" data-testid="ai-copilot-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
          <Robot size={18} weight="bold" className="text-purple-600" />
          Agente AI
        </h3>
        <span className="text-[10px] text-slate-400 italic">
          Impara dai documenti dell'immobile
        </span>
      </div>

      {/* Iter208.31 — Layout 2 colonne: sidebar storico + area chat */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {/* Sidebar storico conversazioni */}
        <aside
          className="md:col-span-1 border rounded-lg bg-white overflow-hidden flex flex-col"
          data-testid="ai-copilot-sessions"
        >
          <button
            type="button"
            onClick={startNewConversation}
            className="w-full flex items-center gap-2 px-3 py-2 border-b bg-purple-50 hover:bg-purple-100 text-purple-800 text-sm font-semibold transition-colors"
            data-testid="ai-copilot-new-session"
          >
            <Plus size={14} weight="bold" />
            Nuova conversazione
          </button>
          <div className="flex-1 overflow-y-auto max-h-72 md:max-h-none">
            {loadingSessions ? (
              <div className="text-center text-[11px] text-slate-400 py-6">Caricamento…</div>
            ) : sessions.length === 0 ? (
              <div className="text-center text-[11px] text-slate-400 py-6 px-2">
                Nessuna conversazione. Iniziane una scrivendo un messaggio.
              </div>
            ) : (
              <ul className="divide-y">
                {sessions.map((s) => {
                  const active = s._id === activeSessionId;
                  return (
                    <li
                      key={s._id}
                      className={
                        "group flex items-center justify-between gap-1 px-2 py-2 text-xs cursor-pointer transition-colors " +
                        (active
                          ? "bg-purple-100 text-purple-900"
                          : "hover:bg-slate-50 text-slate-700")
                      }
                      data-testid={`ai-copilot-session-${s._id}`}
                      data-active={active ? "1" : "0"}
                    >
                      <button
                        type="button"
                        onClick={() => loadSessionHistory(s._id)}
                        className="flex-1 min-w-0 text-left"
                        title={s.title}
                      >
                        <div className="flex items-center gap-1.5">
                          <ChatCircleText size={13} weight={active ? "fill" : "regular"} className={active ? "text-purple-700 shrink-0" : "text-slate-400 shrink-0"} />
                          <span className="truncate font-medium">{s.title || "Nuova conversazione"}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5 pl-4.5">
                          {s.message_count || 0} messaggi
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteSession(s._id); }}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-red-600 transition-opacity"
                        title="Elimina conversazione"
                        data-testid={`ai-copilot-delete-session-${s._id}`}
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Area chat (invariata) */}
        <div className="md:col-span-3 flex flex-col">
      <div
        ref={listRef}
        className={
          "border-2 rounded-lg h-72 overflow-y-auto p-3 space-y-3 transition-colors " +
          (dragOver
            ? "border-purple-500 bg-purple-50/70 border-dashed"
            : "border-slate-200 bg-slate-50/40")
        }
        data-testid="ai-copilot-messages"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {dragOver && (
          <div
            className="pointer-events-none text-center text-sm font-semibold text-purple-700 py-6"
            data-testid="ai-copilot-drop-hint"
          >
            <Paperclip size={22} className="mx-auto mb-1" />
            Rilascia il file qui per aggiungerlo all'Agente
          </div>
        )}
        {!dragOver && messages.length === 0 && (
          <div className="text-center text-slate-400 text-sm mt-8">
            <Sparkle size={26} className="mx-auto mb-2 text-purple-400" />
            Scrivi la tua richiesta o allega un file.<br />
            Es: <em>"Genera il contratto per il cliente Rossi partendo dai documenti."</em>
          </div>
        )}
        {messages.map((m) => {
          if (m.role === "system") {
            return (
              <div key={m._id} className="text-center text-xs text-slate-500 italic">
                {m.body}
              </div>
            );
          }
          const isUser = m.role === "user";
          const isAssistant = !isUser && !m.error;
          return (
            <div key={m._id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
              <div
                className={
                  "max-w-[85%] rounded-lg px-3 py-2 text-sm " +
                  (isUser
                    ? "bg-purple-600 text-white rounded-br-sm whitespace-pre-wrap"
                    : m.error
                    ? "bg-red-50 border border-red-200 text-red-800 rounded-bl-sm whitespace-pre-wrap"
                    : "bg-white border rounded-bl-sm")
                }
                data-testid={`ai-copilot-msg-${m.role}`}
              >
                {isAssistant ? (
                  (() => {
                    // Iter209aj — Se il body contiene `[[OPTIONS:...]]`,
                    // rimuoviamo il marker dal testo e renderizziamo la
                    // domanda + una riga di bottoni cliccabili. Quando
                    // l'utente clicca, mandiamo l'opzione come messaggio
                    // e nascondiamo i bottoni marcando `optionsUsed`.
                    // Iter209ak — Rimuovi SEMPRE eventuali marker
                    // `[[GENERATE_DOC:...]]` dal testo visibile, sia
                    // durante lo streaming token per token (potrebbe
                    // apparire parzialmente) sia a fine risposta.
                    const rawBody = stripGenerateDocMarker(m.body || "");
                    const parsed = m.streaming ? null : parseOptionsMarker(rawBody);
                    const displayBody = parsed
                      ? [parsed.before, parsed.question, parsed.after]
                          .filter(Boolean)
                          .join("\n\n")
                      : rawBody;
                    const showButtons = parsed && !m.optionsUsed && !loading;
                    return (
                      <>
                  <div className="prose prose-sm max-w-none prose-slate prose-p:my-2 prose-headings:mb-2 prose-headings:mt-3 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-strong:text-slate-900 prose-strong:font-semibold prose-a:text-purple-700">
                    <ReactMarkdown
                      components={{
                      // Iter190/203 — link Markdown:
                      // - link interni (`/properties/...`) → naviga via SPA e
                      //   scrolla alla sezione documenti (`?tab=documents`).
                      // - link esterni → nuova tab con noopener.
                      a: ({ node, href, children, ...props }) => {
                        const isInternal = typeof href === "string" && href.startsWith("/");
                        if (isInternal) {
                          return (
                            <a
                              {...props}
                              href={href}
                              className="text-purple-700 underline font-semibold hover:text-purple-800"
                              data-testid="ai-copilot-internal-link"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(href);
                                // Se il link punta alla sezione documenti, prova a scrollare
                                if (href.includes("tab=documents")) {
                                  setTimeout(() => {
                                    const el = document.getElementById("documents-section")
                                      || document.querySelector('[data-section="documents"]');
                                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                  }, 300);
                                }
                              }}
                            >
                              {children}
                            </a>
                          );
                        }
                        return <a {...props} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                      },
                    }}
                  >
                    {displayBody}
                  </ReactMarkdown>
                  </div>
                        {showButtons && (
                          <div
                            className="mt-2 flex flex-wrap gap-2"
                            data-testid={`ai-copilot-options-${m._id}`}
                          >
                            {parsed.options.map((opt, i) => (
                              <button
                                key={`${m._id}-opt-${i}`}
                                type="button"
                                onClick={() => {
                                  setMessages((ms) => ms.map((mm) => mm._id === m._id
                                    ? { ...mm, optionsUsed: true }
                                    : mm));
                                  send(opt);
                                }}
                                className="text-xs font-medium px-3 py-1.5 rounded-full border border-purple-500 bg-white text-purple-700 hover:bg-purple-50 hover:border-purple-600 transition-colors"
                                data-testid={`ai-copilot-option-btn-${i}`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()
                ) : (
                  m.body
                )}
                {m.attachment && m.attachment.filename && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <a
                      href={m.attachment.signed_url || m.attachment.url}
                      download={m.attachment.filename}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md px-3 py-2 transition-colors group"
                      data-testid="ai-copilot-attachment"
                    >
                      <FileDoc size={22} weight="fill" className="text-blue-700 shrink-0" />
                      <span className="text-sm font-medium text-slate-900 truncate max-w-[220px]">
                        {m.attachment.filename}
                      </span>
                      <DownloadSimple size={14} weight="bold" className="text-blue-700 group-hover:translate-y-0.5 transition-transform" />
                    </a>
                  </div>
                )}
                {/* Iter209dzj — Badge "In elaborazione" per messaggi
                    assistente con status=generating caricati dalla history
                    (es. dopo un refresh della pagina mentre il backend sta
                    ancora completando la generazione documento). Si auto-
                    nasconde quando lo stato passa a completed/failed via
                    polling di `/ai-chat/doc-status/{message_id}`. */}
                {!isUser && m.status === "generating" && !m.streaming && (
                  <div
                    className="mt-2 pt-2 border-t border-dashed border-purple-200 flex items-center gap-2 text-[11px] italic text-purple-700"
                    data-testid={`ai-copilot-status-generating-${m._id}`}
                  >
                    <ArrowClockwise size={12} className="animate-spin shrink-0" />
                    <span>
                      Documento in elaborazione
                      {m.generation?.title ? ` — "${m.generation.title}"` : ""}…
                    </span>
                  </div>
                )}

                {/* Iter209v — Strip di progress durante la generazione doc.
                    Mostra la narrazione della fase (reading_documents /
                    writing / finalizing) sotto il body, solo mentre lo
                    stream è in corso.
                    Iter209w — Aggiunta una progress bar sottile che si
                    riempie in base allo stage per un feedback visivo
                    immersivo (25% reading, 60% writing, 90% finalizing). */}
                {m.streaming && m.progress && m.progress.message && (
                  <div
                    className="mt-2 pt-2 border-t border-dashed border-purple-200"
                    data-testid={`ai-copilot-progress-${m.progress.stage}`}
                  >
                    <div className="flex items-center gap-2 text-[11px] italic text-purple-700 mb-1.5">
                      <ArrowClockwise size={12} className="animate-spin shrink-0" />
                      <span data-testid="ai-copilot-progress-message">{m.progress.message}</span>
                    </div>
                    <div
                      className="h-1 w-full bg-purple-100 rounded-full overflow-hidden"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={
                        m.progress.stage === "reading_documents" ? 25 :
                        m.progress.stage === "writing" ? 60 :
                        m.progress.stage === "finalizing" ? 90 : 10
                      }
                      data-testid="ai-copilot-progress-bar"
                    >
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 rounded-full transition-[width] duration-700 ease-out"
                        style={{
                          width: (
                            m.progress.stage === "reading_documents" ? "25%" :
                            m.progress.stage === "writing" ? "60%" :
                            m.progress.stage === "finalizing" ? "90%" : "10%"
                          ),
                        }}
                        data-testid={`ai-copilot-progress-bar-fill-${m.progress.stage}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {loading && (
          <div className="flex justify-start" data-testid="ai-copilot-loading">
            <div className="bg-white border rounded-lg px-3 py-2 text-sm flex items-center gap-2 text-slate-600 max-w-[85%]">
              <ArrowClockwise size={14} className="animate-spin shrink-0" />
              <span className={longWait ? "text-purple-700" : ""}>
                {longWait
                  ? "L'AI sta elaborando un documento lungo, potrebbe volerci un minuto…"
                  : "L'Agente sta pensando…"}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid="ai-copilot-file-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="h-10 w-10 shrink-0 rounded border border-slate-200 bg-white hover:bg-purple-50 flex items-center justify-center text-slate-600 hover:text-purple-700 disabled:opacity-50"
          title="Allega documento"
          aria-label="Allega file all'Agente"
          data-testid="ai-copilot-upload"
        >
          {uploading ? <ArrowClockwise size={16} className="animate-spin" /> : <Paperclip size={16} />}
        </button>
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Es. Generami il contratto per questo cliente…"
          rows={1}
          disabled={loading}
          className="flex-1 text-sm resize-none min-h-[40px]"
          data-testid="ai-copilot-input"
        />
        <Button
          type="button"
          onClick={send}
          disabled={loading || !input.trim()}
          className="bg-purple-600 hover:bg-purple-700 text-white h-10"
          data-testid="ai-copilot-send"
        >
          <PaperPlaneRight size={16} weight="bold" />
        </Button>
      </div>
      <div className="text-[10px] text-slate-400 mt-1">
        L'Agente vede solo i documenti di questo immobile.
      </div>
        </div>
      </div>
    </div>
  );
}
